import axios from 'axios'

export type TrustpilotRatings = {
  starsAverage: number
  numberOfReviews: {
    total: number
    oneStar: number
    twoStars: number
    threeStars: number
    fourStars: number
    fiveStars: number
  }
}

export type TrustpilotReview = {
  id?: string
  stars: number
  content: string
  createdAt: string
  consumer: { displayName: string }
  isImported: boolean
}

export type TrustpilotReviews = {
  reviews: TrustpilotReview[]
  count: number
}

export const REVIEWS_DISPLAY_THRESHOLD = 10

export function formatDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return `${String(d.getDate()).padStart(2, '0')}/${String(
    d.getMonth() + 1
  ).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`
}

const headers = {
  'x-api-key': process.env.TRUSTPILOT_REVIEWS_API_KEY as string
}

// Build-time Trustpilot calls occasionally hit transient network/rate-limit
// blips, so retry a couple of times with backoff before giving up.
async function requestWithRetry<T>(
  request: () => Promise<T>,
  retries = 2,
  delayMs = 500
): Promise<T> {
  try {
    return await request()
  } catch (error) {
    if (retries <= 0) throw error
    await new Promise(resolve => setTimeout(resolve, delayMs))
    return requestWithRetry(request, retries - 1, delayMs * 2)
  }
}

export function getTrustpilotRatings(
  productType: string
): Promise<TrustpilotRatings> {
  return requestWithRetry(() =>
    axios
      .get(`${process.env.TRUSTPILOT_REVIEWS_API_DOMAIN}/ratings`, {
        params: { productType },
        headers
      })
      .then(({ data }) => data)
  )
}

export function getTrustpilotReviews(
  productType: string,
  page = 1,
  pageSize = 20
): Promise<TrustpilotReviews> {
  return requestWithRetry(() =>
    axios
      .get(`${process.env.TRUSTPILOT_REVIEWS_API_DOMAIN}/reviews`, {
        params: { productType, page, pageSize },
        headers
      })
      .then(({ data }) => data)
  )
}

export function getTrustpilotBestReviews(
  productType: string,
  numReviews = 10
): Promise<TrustpilotReviews> {
  return requestWithRetry(() =>
    axios
      .get(`${process.env.TRUSTPILOT_REVIEWS_API_DOMAIN}/best-reviews`, {
        params: { productType, numReviews },
        headers
      })
      .then(({ data }) => data)
  )
}
